import { Interior, Time } from '@/enums';
import type { IEntityTag, IMaterialObject, IMaterialObjectNested } from '@/types';

/**
 * The location and sublocation a heading NAMES, matched case-insensitively —
 * the sublocation only among its own location's children.
 */
export function findLocationByName(
    locations: readonly IMaterialObject[],
    location: string,
    sublocation: string,
): { location?: IMaterialObject; sublocation?: IMaterialObjectNested } {
    const same = (a: string | undefined, b: string) => a?.toUpperCase().trim() === b.toUpperCase().trim();
    const matched = location ? locations.find(loc => same(loc.name, location)) : undefined;
    const sub = matched && sublocation ? matched.nested?.find(nested => same(nested.name, sublocation)) : undefined;
    return { ...(matched ? { location: matched } : {}), ...(sub ? { sublocation: sub } : {}) };
}

/**
 * Find the most specific location match from scene tags (sublocation > location).
 * Popover attaches both parent location + sublocation tags, so we scan all tags
 * before deciding — otherwise the parent location tag is found first and sublocation is missed.
 */
export function findLocationFromTags(
    tags: IEntityTag[],
    locations: IMaterialObject[],
): { location: IMaterialObject; sublocation?: IMaterialObjectNested } | null {
    let matchedLoc: IMaterialObject | null = null;
    let matchedSub: IMaterialObjectNested | null = null;

    for (const tag of tags) {
        for (const loc of locations) {
            const sub = loc.nested?.find(s => s.entity_tag.id === tag.id);
            if (sub) {
                matchedLoc = loc;
                matchedSub = sub;
                break;
            }
            if (loc.entity_tag.id === tag.id && !matchedLoc) {
                matchedLoc = loc;
            }
        }
        if (matchedSub) break;
    }

    if (!matchedLoc) return null;
    return matchedSub ? { location: matchedLoc, sublocation: matchedSub } : { location: matchedLoc };
}

export interface ParsedSceneHeading {
    interior: Interior | null;
    location: string;
    sublocation: string;
    time: Time | null;
}

/**
 * Parse a Fountain scene heading like "INT. COFFEE SHOP. - DAY." into parts.
 * Supports both Latin (INT, EXT, NAT) and Cyrillic (ИНТ, НАТ, ПАВ).
 */
export function parseSceneHeading(line: string): ParsedSceneHeading {
    const upper = line.toUpperCase().trim();

    let interior: Interior | null = null;
    // Slash-bearing prefixes must be checked before INT./NAT. so e.g. "INT/EXT" isn't misclassified as plain INT.
    if (
        upper.startsWith('I/N.') ||
        upper.startsWith('I/N ') ||
        upper === 'I/N' ||
        upper.startsWith('I/E.') ||
        upper.startsWith('I/E ') ||
        upper === 'I/E' ||
        upper.startsWith('INT/EXT.') ||
        upper.startsWith('INT/EXT ') ||
        upper === 'INT/EXT' ||
        upper.startsWith('ИНТ/НАТ.') ||
        upper.startsWith('ИНТ/НАТ ') ||
        upper === 'ИНТ/НАТ'
    ) {
        interior = Interior.intExt;
    } else if (upper.startsWith('INT.') || upper.startsWith('INT') || upper.startsWith('ИНТ.') || upper.startsWith('ИНТ')) {
        interior = Interior.int;
    } else if (
        upper.startsWith('EXT.') ||
        upper.startsWith('NAT.') ||
        upper.startsWith('NAT') ||
        upper.startsWith('НАТ.') ||
        upper.startsWith('НАТ') ||
        upper.startsWith('ПАВ.') ||
        upper.startsWith('ПАВ')
    ) {
        interior = Interior.nat;
    }

    const remainder = line.replace(/^(I\/N\.?|I\/E\.?|INT\/EXT\.?|ИНТ\/НАТ\.?|INT\.|EXT\.|NAT\.|ИНТ\.|НАТ\.|ПАВ\.)\s*/i, '').trim();

    // Split into LOCATION / SUBLOCATION / TIME segments without tearing apart
    // hyphenated names (ЖД-ВОКЗАЛ, COCA-COLA). buildSceneHeading dot-terminates
    // every segment ("LOCATION. - TIME."), so prefer the ". - " delimiter when
    // present; fall back to space-padded " - " for standard Fountain input
    // ("INT. HOUSE - DAY"). A bare in-name hyphen matches neither.
    //
    // An empty location is written as nothing at all ("INT. - DAY."), which
    // leaves the remainder opening on the dash: that segment is the location.
    const locationless = /^-\s+/.test(remainder);
    const rest = locationless ? remainder.replace(/^-\s+/, '') : remainder;
    const delimiter = /\.\s*-\s+/.test(rest) ? /\.\s*-\s+/ : /\s+-\s+/;
    const parts = [...(locationless ? [''] : []), ...rest.split(delimiter).map(p => p.trim())];

    let location = '';
    let sublocation = '';
    let time: Time | null = null;

    if (parts.length >= 1) {
        location = (parts[0] ?? '').replace(/\.\s*$/, '').trim();
    }

    if (parts.length === 2) {
        const secondPart = (parts[1] ?? '').replace(/\.\s*$/, '').trim();
        const secondPartUpper = secondPart.toUpperCase();

        if (secondPartUpper.includes('DAY') || secondPartUpper === 'Д') {
            time = Time.day;
        } else if (secondPartUpper.includes('NIGHT') || secondPartUpper === 'Н') {
            time = Time.night;
        } else {
            sublocation = secondPart;
        }
    } else if (parts.length >= 3) {
        sublocation = (parts[1] ?? '').replace(/\.\s*$/, '').trim();
        const timeStr = (parts[2] ?? '')
            .replace(/\.\s*$/, '')
            .trim()
            .toUpperCase();
        if (timeStr.includes('DAY') || timeStr === 'Д') {
            time = Time.day;
        } else if (timeStr.includes('NIGHT') || timeStr === 'Н') {
            time = Time.night;
        }
    }

    return { interior, location, sublocation, time };
}

/**
 * Reconstruct a Fountain scene heading from parts.
 * Format: INT. LOCATION. - SUBLOCATION. - TIME.
 * Always uses English prefixes for data consistency (INT/NAT/DAY/NIGHT) — UI shows translated labels.
 */
export function buildSceneHeading(interior: Interior | null, location: string, sublocation: string, time: Time | null): string {
    const parts: string[] = [];

    if (interior === Interior.int) {
        parts.push('INT.');
    } else if (interior === Interior.nat) {
        parts.push('NAT.');
    } else if (interior === Interior.intExt) {
        parts.push('I/N.');
    }

    if (location) {
        parts.push(`${location.toUpperCase()}.`);
    }

    if (sublocation) {
        parts.push(`- ${sublocation.toUpperCase()}.`);
    }

    if (time === Time.day) {
        parts.push('- DAY.');
    } else if (time === Time.night) {
        parts.push('- NIGHT.');
    }

    // With no parts, fall back to a bare forced-heading dot so line 0 stays a
    // SCENE_HEADING block (an empty string would be reclassified as action).
    if (parts.length === 0) {
        return '.';
    }

    return parts.join(' ');
}
